import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { storeToRefs } from 'pinia';
import UpdateAggregateBusinessModal from './components/UpdateAggregateBusinessModal.vue';
import { useUpdateBusinessModalStore } from './useUpdateBusinessModalStore';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { i18n } from '@/i18n';
import { computed, Fragment, h, onMounted, VNode, watch } from 'vue';
import { isSuccess } from '@/coreDomainsModule/uiLoadingState';
import { checkChildBusinessTableContentHeader, commonModalTextMapper, ModalType, ProcessType } from './constants';
import UpdateBaseBusinessModal from './components/UpdateBaseBusinessModal.vue';
import NewBaseTableContent from '../BaseTableContent/NewBaseTableContent.vue';
import CreateBaseBusinessModal from './components/CreateBaseBusinessModal.vue';
import { showInfoModal } from '../components/InfoModal/InfoModalManager';
import { waitLoading } from '../components/InfoModal/waitLoading';


export default /*@__PURE__*/_defineComponent({
  __name: 'UpdateBusinessModals',
  props: {
    isOpeningAggregateBusinessEdit: { type: Boolean },
    isOpeningBaseBusinessEdit: { type: Boolean },
    isCreateMode: { type: Boolean },
    item: { default: undefined }
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { $reset, fetchChildBusiness, putEditedAggregateBusiness, putEditedBaseBusiness, createNewAggregateBusiness, createNewBaseBusiness } =
  store;

const props = __props;

const emits = __emit;

onMounted(() => {
  $reset();
});

watch(
  () => props.isOpeningAggregateBusinessEdit,
  (n) => {
    if (n) state.value.modalState.aggregateBusinessEdit = true;
  }
);

watch(
  () => props.isOpeningBaseBusinessEdit,
  (n) => {
    if (n) state.value.modalState.baseBusinessEdit = true;
  }
);

watch(
  () => props.isCreateMode,
  (n) => {
    if (n) state.value.isCreateMode = n;
  }
);

/**
 * モーダルを閉じる処理
 * @param modal - モーダルの種類
 * @param isNext - 次へ進むかどうかを選択する
 */
const closeFirstModal = async (modal: string, isNext?: boolean) => {
  /**
   * 全てのモーダルが出ていないか確認する関数
   */
  const _onAllModalClosed = () => {
    emits('close');
    $reset();
  };

  /**
   * 実際にAPIに投げる処理を行う部分
   * @param processFunc - 実際の処理関数
   * @param processType - 処理のタイプ
   * @param noPostProcessingOnSuccess - API通信正常終了時の後処理を行わない
   */
  const _callApi = async (
    processFunc: () => Promise<ModalType>,
    processType: ProcessType,
    noPostProcessingOnSuccess = false
  ): Promise<boolean> => {
    const result = await waitLoading(processFunc(), _checkUndefinedText(commonModalTextMapper[processType].loading?.title), '');
    if (result === 'success') {
      if (!noPostProcessingOnSuccess) {
        await showInfoModal({
          modalTitle: _checkUndefinedText(commonModalTextMapper[processType].success?.title),
          backButtonLabel: t('確認'),
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        $reset();
        emits('success');
      }
      return true;
    } else if (result === 'noParentError') {
      const isCreateAggregateBusiness = await showInfoModal({
        modalTitle: _checkUndefinedText(commonModalTextMapper[processType]['noParentError']?.title),
        modalDescription: _checkUndefinedText(commonModalTextMapper[processType]['noParentError']?.description),
        nextButtonLabel: t('OK'),
        backButtonLabel: t('キャンセル'),
        enableOutsideClick: false,
      });
      if (isCreateAggregateBusiness) {
        state.value.isCreatingBoth = true;
        state.value.modalState.aggregateBusinessEdit = true;
        return true;
      } else {
        return false;
      }
    } else {
      await showInfoModal({
        modalTitle: _checkUndefinedText(commonModalTextMapper[processType][result ?? 'commonError']?.title),
        modalDescription: _checkUndefinedText(commonModalTextMapper[processType][result ?? 'commonError']?.description),
        backButtonLabel: t('確認'),
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      return false;
    }
  };

  /**
   * テキストがundefinedだった場合に空文字に変換する関数
   * @param text - テキスト
   */
  const _checkUndefinedText = (text?: string) => {
    return text ? t(text) : '';
  };

  if (!state.value.modalState.aggregateBusinessEdit && !state.value.modalState.baseBusinessEdit) return;

  state.value.modalState.aggregateBusinessEdit = false;
  state.value.modalState.baseBusinessEdit = false;
  if (isNext) {
    let apiRes = false;
    if (state.value.isCreatingBoth) {
      apiRes = await _callApi(createNewAggregateBusiness, 'create', true);
      if (!apiRes) {
        state.value.modalState.aggregateBusinessEdit = true;
        return;
      }
      apiRes = await _callApi(createNewBaseBusiness, 'create');
      if (!apiRes) {
        state.value.modalState.baseBusinessEdit = true;
      }
      return;
    } else if (modal === 'aggregateBusinessEdit') {
      if (props.isCreateMode) {
        apiRes = await _callApi(createNewAggregateBusiness, 'create');
      } else {
        await waitLoading(fetchChildBusiness(), undefined, '');
        const confirmWithChildBusinessResult = await showInfoModal({
          modalTitle: t('編集'),
          modalDescription: getChildBusinessModalDescription.value,
          modalContent: h(NewBaseTableContent, {
            propTableContentProperties: checkChildBusinessTableContentHeader,
            propDisplayObjects: state.value.childBusiness.tableContentItems,
            propLoadingStatus: state.value.childBusiness.loadingState,
            propTableType: 'editAggregateBusinessChild',
            style: {
              maxHeight: '396px',
              minHeight: 0,
              color: '$textSecondary',
            },
          }),
          modalWidth: 1300,
          backButtonLabel: t('キャンセル'),
          nextButtonLabel: t('適用'),
          enableOutsideClick: false,
        });

        if (confirmWithChildBusinessResult) {
          apiRes = await _callApi(putEditedAggregateBusiness, 'edit');
        }
      }
      if (!apiRes) state.value.modalState.aggregateBusinessEdit = true;
      return;
    } else if (modal === 'baseBusinessEdit') {
      if (props.isCreateMode) {
        apiRes = await _callApi(createNewBaseBusiness, 'create');
      } else {
        apiRes = await _callApi(putEditedBaseBusiness, 'edit');
      }
      if (!apiRes) state.value.modalState.baseBusinessEdit = true;
      return;
    }
  } else {
    _onAllModalClosed();
    return;
  }
};

const getChildBusinessModalDescription = computed(() => {
  const allAvailableNumber = state.value.childBusiness.pagingState.totalResultsAvailable ?? 0;
  let res: Array<string | VNode> = [];
  if (isSuccess(state.value.childBusiness.loadingState.status)) {
    res.push(t('ここから出荷している取引', { amount: allAvailableNumber ? allAvailableNumber.toString() : '全' }));
    if (allAvailableNumber > 50) {
      res = [t('該当する取引の先頭50件のみ表示しています。'), h('br'), ...res];
    }
  }
  return h(Fragment, null, res);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(UpdateAggregateBusinessModal, {
      item: _ctx.item,
      onClose: closeFirstModal
    }, null, 8, ["item"]),
    _createVNode(UpdateBaseBusinessModal, {
      item: _ctx.item,
      onClose: closeFirstModal
    }, null, 8, ["item"]),
    _createVNode(CreateBaseBusinessModal, {
      item: _ctx.item,
      onClose: closeFirstModal
    }, null, 8, ["item"])
  ], 64))
}
}

})